import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Check, ChevronRight, ChefHat, Building2, Users } from '@/components/icons';
import { useAuth } from '@/contexts/AuthContext';
import KlaroLogo from '@/components/haccp/KlaroLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STEPS = [
  { key: 'company', label: 'Dati azienda', icon: Building2 },
  { key: 'recipe', label: 'Prima ricetta', icon: ChefHat },
  { key: 'invite', label: 'Primo operatore', icon: Users },
];

export default function OnboardingWizard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState({ name: '', vat_number: '', address: '', phone: '' });
  const [recipe, setRecipe] = useState({ name: '', category: '', notes: '' });
  const [invite, setInvite] = useState({ name: '', email: '' });

  useEffect(() => {
    if (!user) return;
    // Non-admin or already completed: skip wizard entirely.
    if (user.role !== 'admin' || user.onboarding_completed) {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetch(`${BACKEND_URL}/api/companies/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(c => c && setCompany({ name: c.name || '', vat_number: c.vat_number || '', address: c.address || '', phone: c.phone || '' }));
  }, [user, navigate]);

  const finish = async () => {
    await fetch(`${BACKEND_URL}/api/auth/onboarding-complete`, { method: 'POST', credentials: 'include' });
    setUser({ ...(user || {}), onboarding_completed: true });
    toast.success('Onboarding completato');
    navigate('/dashboard', { replace: true });
  };

  const saveCompany = async () => {
    if (!company.name.trim()) return toast.error('Nome azienda obbligatorio');
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/companies/me`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(company),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Salvataggio non riuscito');
      toast.success('Dati aziendali salvati');
      setStep(1);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const saveRecipe = async () => {
    if (!recipe.name.trim()) return toast.error('Nome ricetta obbligatorio');
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/recipes/`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: recipe.name.trim(),
          category: recipe.category.trim() || 'Generale',
          ingredients: [],
          operations: [],
          notes: recipe.notes.trim(),
          produce_type: 'finished',
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Creazione ricetta non riuscita');
      toast.success('Prima ricetta creata');
      setStep(2);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const sendInvite = async () => {
    if (!invite.name.trim() || !invite.email.trim()) return toast.error('Nome ed email obbligatori');
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/invitations/`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: invite.name.trim(), email: invite.email.trim().toLowerCase(), role: 'operator' }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Invito non inviato');
      toast.success('Invito inviato');
      await finish();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const skip = async () => {
    if (step < STEPS.length - 1) return setStep(step + 1);
    await finish();
  };

  if (!user) return null;
  const StepIcon = STEPS[step].icon;

  return (
    <div className="min-h-screen bg-slate-50 py-6 sm:py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex flex-col items-center text-center">
          <KlaroLogo />
          <h1 className="mt-4 font-heading text-2xl sm:text-3xl font-semibold">Benvenuto in Klaro</h1>
          <p className="mt-1 text-sm text-[#475467]">Configura il tuo laboratorio in 3 passi. Puoi saltare i passaggi opzionali.</p>
        </div>

        <div className="surface p-3 mb-4 flex items-center gap-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div data-testid={`wizard-step-${s.key}`} className={`flex-1 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium ${i === step ? 'bg-primary text-white' : i < step ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500'}`}>
                {i < step ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                <span className="truncate">{i + 1}. {s.label}</span>
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block" />}
            </React.Fragment>
          ))}
        </div>

        <div className="surface p-5 sm:p-7">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><StepIcon className="w-6 h-6" /></div>
            <div>
              <h2 className="font-heading text-xl font-semibold">{STEPS[step].label}</h2>
              <p className="text-sm text-[#475467] mt-0.5">
                {step === 0 && 'Verifica i dati fiscali della tua azienda. Li useremo su etichette e PDF.'}
                {step === 1 && 'Crea una ricetta di partenza: potrai aggiungere ingredienti e passaggi dopo.'}
                {step === 2 && 'Invita un operatore: riceverà via email un link per attivare l\'account.'}
              </p>
            </div>
          </div>

          {step === 0 && (
            <div className="space-y-4">
              <div><Label>Nome azienda *</Label><Input data-testid="wiz-company-name" value={company.name} onChange={e => setCompany({ ...company, name: e.target.value })} /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>P.IVA</Label><Input data-testid="wiz-company-vat" value={company.vat_number} onChange={e => setCompany({ ...company, vat_number: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input data-testid="wiz-company-phone" value={company.phone} onChange={e => setCompany({ ...company, phone: e.target.value })} /></div>
              </div>
              <div><Label>Indirizzo</Label><Input data-testid="wiz-company-address" value={company.address} onChange={e => setCompany({ ...company, address: e.target.value })} /></div>
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button data-testid="wiz-company-save" variant="success" className="flex-1" onClick={saveCompany} disabled={saving}>{saving ? 'Salvo…' : 'Salva e continua'}</Button>
                <Button variant="ghost" onClick={skip}>Salta</Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div><Label>Nome ricetta *</Label><Input data-testid="wiz-recipe-name" value={recipe.name} onChange={e => setRecipe({ ...recipe, name: e.target.value })} placeholder="Es. Pane bianco" /></div>
              <div><Label>Categoria</Label><Input data-testid="wiz-recipe-category" value={recipe.category} onChange={e => setRecipe({ ...recipe, category: e.target.value })} placeholder="Es. Panificati" /></div>
              <div><Label>Note</Label><Textarea data-testid="wiz-recipe-notes" rows={3} value={recipe.notes} onChange={e => setRecipe({ ...recipe, notes: e.target.value })} placeholder="Descrizione, tempi, temperature…" /></div>
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button data-testid="wiz-recipe-save" variant="success" className="flex-1" onClick={saveRecipe} disabled={saving}>{saving ? 'Creo…' : 'Crea e continua'}</Button>
                <Button variant="ghost" onClick={skip}>Salta</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">L'operatore riceverà una email con un link valido 72 ore per scegliere la password.</div>
              <div><Label>Nome operatore *</Label><Input data-testid="wiz-invite-name" value={invite.name} onChange={e => setInvite({ ...invite, name: e.target.value })} /></div>
              <div><Label>Email *</Label><Input data-testid="wiz-invite-email" type="email" value={invite.email} onChange={e => setInvite({ ...invite, email: e.target.value })} /></div>
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button data-testid="wiz-invite-send" variant="success" className="flex-1" onClick={sendInvite} disabled={saving}>{saving ? 'Invio…' : 'Invia invito e concludi'}</Button>
                <Button variant="ghost" onClick={skip}>Salta e concludi</Button>
              </div>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-[#98A2B3]">Puoi rifare tutto anche dopo dalla dashboard.</p>
      </div>
    </div>
  );
}
